import { useCallback, useEffect, useState } from "react";
import { Card } from "@/src/components/wrappers/Card";
import { Button } from "@/src/components/ui/button";
import { fetch } from "@/src/lib/fetch";
import { cn } from "@/src/lib/utils";
import { useBookingReviewAction } from "@/src/lib/bookingReviewMutation";

/**
 * Post-charter review (brief §13). Hidden until the charter starts (page-level
 * gate); "Not yet returned" until the survey lands; then the full review with
 * publish / flag / forward. What the survey doesn't ask is said, not faked.
 */

type Review = {
  surveyId: string;
  charterType: string;
  submittedAt: string | null;
  overall: number | null;
  categories: { key: string; label: string; score: number | null; answer: string | null; comment: string | null }[];
  incidents: { key: string; label: string; answer: string; comment: string | null }[];
  comments: { key: string; label: string; text: string }[];
  quote: { label: string; text: string } | null;
  attribution: { name: string | null; party: number | null; yacht: string | null; start: string | null };
  tags: string[];
  published: { at: string; by: string | null } | null;
  flagged: { at: string; by: string | null; reason: string | null; resolvedAt: string | null } | null;
  held: boolean;
  forwarded: { at: string; to: string | null } | null;
  publicVisible: boolean;
  canPublish: boolean;
  isPoor: boolean;
  crewEmail: string | null;
};
type State = { status: "not_returned" | "returned"; charterStart: string | null; notCollected: string[]; review: Review | null };

const NOT_COLLECTED_LABELS: Record<string, string> = {
  wouldRecommend: "would recommend",
  wouldRebook: "would rebook",
  highlights: "highlights",
  testimonialConsent: "testimonial consent",
};
const day = (s: string | null | undefined) => (s ? new Date(s).toLocaleDateString("en-GB") : "");

export function ReviewSection({ bookingId }: { bookingId: string }) {
  const [state, setState] = useState<State | null>(null);
  const load = useCallback(() => {
    fetch<{ data: State }>(`/api/bookings/${bookingId}/review`).then((r: any) => setState(r.data)).catch(() => setState(null));
  }, [bookingId]);
  useEffect(load, [load]);
  const act = useBookingReviewAction(bookingId, load);

  if (!state) return null;
  const r = state.review;

  if (state.status === "not_returned" || !r) {
    return (
      <Card className="w-full">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold">Post-charter review</h3>
          <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-600">Not yet returned</span>
        </div>
        <p className="mt-2 text-sm text-slate-500">The survey goes out after the charter; the review appears here once the client submits it.</p>
      </Card>
    );
  }

  const busy = act.isPending;
  return (
    <Card className="flex w-full flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-lg font-semibold">Post-charter review</h3>
            {r.publicVisible && <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">On the yacht page</span>}
            {r.held && <span className="rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-medium text-red-700">Held — flagged for follow-up</span>}
            {r.published && r.held && <span className="text-xs text-slate-500">(published, but hidden while flagged)</span>}
            {r.forwarded && <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">Forwarded {day(r.forwarded.at)}</span>}
          </div>
          <p className="text-xs text-slate-500">
            Submitted {day(r.submittedAt)}
            {r.attribution.name ? ` by ${r.attribution.name}` : ""}
            {r.attribution.party ? ` · party of ${r.attribution.party}` : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {r.published ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => act.mutate({ action: "unpublish" })}>Unpublish</Button>
          ) : (
            <Button size="sm" disabled={busy || !r.canPublish} title={r.canPublish ? "" : "Resolve the follow-up first"} onClick={() => act.mutate({ action: "publish" })}>
              Publish to yacht page
            </Button>
          )}
          {r.held ? (
            <Button size="sm" variant="outline" disabled={busy} onClick={() => act.mutate({ action: "resolve" })}>Mark resolved</Button>
          ) : (
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                const reason = window.prompt("Why does this review need follow-up? (holds it off the public page)");
                if (reason !== null) act.mutate({ action: "flag", reason });
              }}
            >
              Flag for follow-up
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            disabled={busy || !r.crewEmail}
            title={r.crewEmail ? `Emails ${r.crewEmail}` : "No operator email on file"}
            onClick={() => window.confirm(`Email this review to ${r.crewEmail}?`) && act.mutate({ action: "forward" })}
          >
            Forward to crew
          </Button>
        </div>
      </div>

      {r.isPoor && !r.held && (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-800">
          This is a poor review. Flag it for follow-up to keep it off the public page while it's looked into.
        </p>
      )}
      {r.flagged && (
        <p className={cn("rounded-md p-3 text-sm", r.held ? "bg-red-50 text-red-800" : "bg-slate-50 text-slate-600")}>
          Flagged {day(r.flagged.at)}{r.flagged.by ? ` by ${r.flagged.by}` : ""}{r.flagged.reason ? `: “${r.flagged.reason}”` : ""}
          {r.flagged.resolvedAt ? ` · resolved ${day(r.flagged.resolvedAt)}` : ""}
        </p>
      )}

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-[auto_1fr]">
        <div className="flex flex-col items-center justify-center rounded-lg bg-slate-50 px-8 py-4">
          <span className="text-4xl font-semibold tabular-nums">{r.overall ?? "—"}</span>
          <span className="text-xs text-slate-500">overall / 5</span>
        </div>
        <ul className="flex flex-col gap-2">
          {r.categories.map((c) => (
            <li key={c.key}>
              <div className="flex items-baseline justify-between text-sm">
                <span className="font-medium">{c.label}</span>
                <span className="tabular-nums text-slate-500">{c.score ?? "—"}{c.score != null ? " / 5" : ""}</span>
              </div>
              <div className="mt-1 h-1.5 w-full rounded bg-slate-100">
                <div className={cn("h-1.5 rounded", (c.score ?? 0) >= 4 ? "bg-green-500" : (c.score ?? 0) >= 3 ? "bg-amber-400" : "bg-red-400")} style={{ width: `${((c.score ?? 0) / 5) * 100}%` }} />
              </div>
              {c.answer && <p className="mt-0.5 text-xs text-slate-500">{c.answer}{c.comment ? ` — “${c.comment}”` : ""}</p>}
            </li>
          ))}
        </ul>
      </div>

      {r.quote && (
        <blockquote className="border-l-4 border-slate-200 pl-4 text-sm italic text-slate-700">
          “{r.quote.text}”
          <footer className="mt-1 text-xs not-italic text-slate-500">
            — {r.attribution.name ?? "the client"}{r.attribution.party ? `, party of ${r.attribution.party}` : ""}{r.attribution.yacht ? `, ${r.attribution.yacht}` : ""} · {r.quote.label}
          </footer>
        </blockquote>
      )}

      {r.incidents.length > 0 && (
        <dl className="text-sm">
          {r.incidents.map((i) => (
            <div key={i.key} className="flex gap-3 py-0.5">
              <dt className="text-slate-500">{i.label}</dt>
              <dd className="font-medium">{i.answer}{i.comment ? ` — ${i.comment}` : ""}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {r.tags.map((t) => (
          <span key={t} className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", /poor|issues:/i.test(t) ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-700")}>{t}</span>
        ))}
        <span className="text-xs text-slate-400">
          Not asked by the survey: {state.notCollected.map((k) => NOT_COLLECTED_LABELS[k] ?? k).join(", ")}.
        </span>
      </div>
    </Card>
  );
}
